import React from 'react';
import { FiEdit3, FiFileText, FiHelpCircle } from 'react-icons/fi';
import { BsMegaphone } from 'react-icons/bs';
import { MdOutlineWarningAmber } from 'react-icons/md';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { PiUser, PiUsersThree } from 'react-icons/pi';
import { BsSuitClub } from 'react-icons/bs';
import { composeContentTypeOption } from '../support/openDiscussions/config/postComposeConstants';
import { buildGenericDiscussionPermalinkPath } from '../support/openDiscussions/lib/discussionPermalink';
import {
  applyProfileAvatarImgFallback,
  buildProfilePictureUrlForMode,
} from '../MainHeader/profilePictureUrl';

const baseUrlFallback = process.env.REACT_APP_BASE_URL || '';

export function PostContentTypeIcon({ postContentType, size = 20 }) {
  const opt = composeContentTypeOption(postContentType);
  const tone = opt?.iconTone || 'story';
  const className = `notification-post-type-icon notification-post-type-icon--${tone}`;
  const iconMap = {
    story: <FiEdit3 className={className} size={size} aria-hidden />,
    question: <FiHelpCircle className={className} size={size} aria-hidden />,
    article: <FiFileText className={className} size={size} aria-hidden />,
    announcement: <BsMegaphone className={className} size={size} aria-hidden />,
  };
  return iconMap[tone] || iconMap.story;
}

export function notificationActorUserId(notification) {
  const md = notification?.metadata || {};
  return String(
    md.actorUserId ||
      md.fromUserId ||
      md.followerUserId ||
      md.voterUserId ||
      md.commenterUserId ||
      md.authorUserId ||
      ''
  ).trim();
}

function notificationAvatarIdentity(notification, profileMode = 'personal') {
  const actorUserId = notificationActorUserId(notification);
  const actorName = String(
    notification?.metadata?.actorName || notification?.metadata?.actorNames?.[0] || ''
  ).trim();
  const title = notificationDisplayTitle(notification);
  return {
    userId: actorUserId || notification?.notificationId || '',
    name: actorName || title || 'Member',
    profileMode:
      notification?.metadata?.actorProfileMode ||
      notification?.metadata?.followerProfileMode ||
      profileMode,
  };
}

/** Colored default avatar for an actor; avoids broken / grey / question-mark icons in the list. */
export function NotificationListAvatar({
  notification,
  size = 20,
  baseUrl = baseUrlFallback,
  profileMode = 'personal',
}) {
  const identity = notificationAvatarIdentity(notification, profileMode);
  const actorPic =
    notification?.metadata?.actorProfilePictureUrl ||
    notification?.metadata?.actorProfilePicture ||
    '';
  const src = buildProfilePictureUrlForMode(actorPic, baseUrl, identity.profileMode, {
    userId: identity.userId,
    name: identity.name,
  });
  return (
    <img
      className="notifications-item-actor-avatar"
      src={src}
      alt=""
      width={size}
      height={size}
      onError={(e) => {
        applyProfileAvatarImgFallback(e.currentTarget, {
          userId: identity.userId,
          name: identity.name,
        });
      }}
    />
  );
}

export function NotificationKindIcon({ notification, size = 20 }) {
  const kind = String(notification?.kind || notification?.metadata?.code || '').trim();
  if (notification?.type === 'action_required') {
    return <MdOutlineWarningAmber className="notification-icon-action" size={size} aria-hidden />;
  }
  if (kind.startsWith('club_') || kind === 'post_moved_club') {
    return <BsSuitClub className="notification-icon-club" size={size} aria-hidden />;
  }
  if (kind.includes('follow')) {
    return <PiUsersThree className="notification-icon-social" size={size} aria-hidden />;
  }
  if (kind === 'new_post_following' || kind === 'post_tagged') {
    return <PiUser className="notification-icon-social" size={size} aria-hidden />;
  }
  if (notification?.postContentType) {
    return <PostContentTypeIcon postContentType={notification.postContentType} size={size} />;
  }
  return <IoInformationCircleOutline className="notification-icon-info" size={size} aria-hidden />;
}

export function profileModeLabel(mode) {
  return mode === 'social' ? 'Social' : 'Personal';
}

const PROFILE_MODE_PAREN_RE = /\s*\((?:personal|social)(?:\s+profile)?\)\s*/gi;

export function stripProfileModeParenthetical(text) {
  return String(text || '')
    .replace(PROFILE_MODE_PAREN_RE, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function notificationDisplayTitle(notification) {
  return stripProfileModeParenthetical(notification?.title || '');
}

export function notificationDisplayMessage(notification) {
  return stripProfileModeParenthetical(notification?.message || '');
}

export function notificationLinkTarget(notification) {
  const slug = notification?.metadata?.discussionSlug || notification?.metadata?.slug || '';
  if (!slug) return null;
  const contentType = String(notification?.postContentType || notification?.metadata?.postContentType || '').trim();
  const typedPath =
    contentType === 'story' ||
    contentType === 'question' ||
    contentType === 'article' ||
    contentType === 'announcement'
      ? buildGenericDiscussionPermalinkPath(slug, contentType)
      : null;
  return typedPath || `/posts/thread/${encodeURIComponent(slug)}`;
}

export const SHELL_HEADER_DROPDOWN_Z_INDEX = 10050;

export const NOTIFICATIONS_DROPDOWN_PORTAL_SELECTOR = '.notifications-dropdown--portal';

export function isNotificationsDropdownPortalTarget(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(target.closest(NOTIFICATIONS_DROPDOWN_PORTAL_SELECTOR));
}
